import { apiEmployeePerformanceReviews } from "@/api/employees/employee-performance-review.api.ts";
import { SubmitParams } from "@/components/questionnaire/questionnaire.types.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeeTodayKeys } from "@/queries/employees/use-employee-today.query.ts";
import { performanceReviewsKeys } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { appKeys } from "@/queries/settings/use-app.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiPerformanceReviewParticipantFilter,
  ApiPerformanceReviewParticipantViewFilter,
  ApiPerformanceReviewsFilter,
} from "@/types/performance-review.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const employeePerformanceReviewKey = createQueryKeys("employeePerformanceReview", {
  my: () => ({
    queryKey: ["my"],
    queryFn: async () => {
      const result = await apiEmployeePerformanceReviews.getMy();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  byId: (employeeId: number, reviewerId: number) => ({
    queryKey: [employeeId, reviewerId],
    queryFn: async () => {
      const result = await apiEmployeePerformanceReviews.getById(employeeId, reviewerId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  form: (employeeId: number, reviewerId: number) => ({
    queryKey: [employeeId, reviewerId],
    queryFn: async () => {
      const result = await apiEmployeePerformanceReviews.getForm(employeeId, reviewerId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data!;
    },
  }),
  searchMySubordinateParticipants: (filter: ApiPerformanceReviewParticipantFilter) => ({
    queryKey: [filter],
  }),
  getMyDirectSubordinate: () => ({
    queryKey: ["getMyDirectSubordinate"],
  }),
  myAwaitingReviewerInvite: () => ({
    queryKey: ["myAwaitingReviewerInvite"],
    queryFn: async () => {
      const result = await apiEmployeePerformanceReviews.getMyAwaitingReviewerInvite();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  searchReviewParticipantViews: (employeeId: number, filter: ApiPerformanceReviewsFilter) => ({
    queryKey: [employeeId, filter],
  }),
  searchMyFeedbacks: (filter: ApiPerformanceReviewParticipantFilter) => ({
    queryKey: [filter],
  }),
});

export const useMyEmployeePerformanceReviews = (enabled: boolean = true) => {
  return useQuery({
    ...employeePerformanceReviewKey.my(),
    enabled: enabled,
  });
};

export const useMyEmployeePerformanceReviewsAwaitingReviewerInvite = (enabled: boolean = true) => {
  return useQuery({
    ...employeePerformanceReviewKey.myAwaitingReviewerInvite(),
    enabled: enabled,
  });
};

export const useEmployeePerformanceReviewByReviewerId = ({
  employeeId,
  reviewerId,
  enabled = true,
}: {
  employeeId: number;
  reviewerId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    ...employeePerformanceReviewKey.byId(employeeId, reviewerId),
    enabled: enabled,
  });
};

export const useEmployeePerformanceReviewForm = ({
  employeeId,
  reviewerId,
  enabled = true,
}: {
  employeeId: number;
  reviewerId: number;
  enabled?: boolean;
}) => {
  return useQuery({
    ...employeePerformanceReviewKey.form(employeeId, reviewerId),
    enabled: enabled,
  });
};

export const useEmployeePerformanceReviewSaveDraft = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ employeeId, questionnaireId, answers }: SubmitParams) {
      const result = await apiEmployeePerformanceReviews.saveDraft(
        employeeId,
        questionnaireId,
        answers
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeePerformanceReviewKey.byId._def,
      });
      queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useEmployeePerformanceReviewSubmitResults = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ employeeId, questionnaireId, answers }: SubmitParams) {
      const result = await apiEmployeePerformanceReviews.submitResults(
        employeeId,
        questionnaireId,
        answers
      );
      if (result.error || !result.data) throw new ApiErrorException(result.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: employeePerformanceReviewKey.byId._def,
      });
      queryClient.invalidateQueries({
        queryKey: performanceReviewsKeys.participantReport._def,
      });
      queryClient.invalidateQueries({
        queryKey: appKeys.menuCounters.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: employeeTodayKeys.getToday._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSearchMySubordinateParticipants = (
  filter: ApiPerformanceReviewParticipantFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeePerformanceReviewKey.searchMySubordinateParticipants(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiEmployeePerformanceReviews.searchMySubordinateParticipants(
        filter,
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useGetMyDirectSubordinate = (enabled: boolean = true) => {
  return useQuery({
    ...employeePerformanceReviewKey.getMyDirectSubordinate(),
    enabled: enabled,
    queryFn: async () => {
      const result = await apiEmployeePerformanceReviews.getMyDirectSubordinate();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useSearchMyFeedbacks = (
  filter: ApiPerformanceReviewParticipantFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...employeePerformanceReviewKey.searchMyFeedbacks(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiEmployeePerformanceReviews.searchMyFeedbacks(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useSearchEmployeeReviewParticipantViews = ({
  employeeId,
  filter,
  enabled = true,
}: {
  employeeId: number;
  filter: ApiPerformanceReviewParticipantViewFilter;
  enabled?: boolean;
}) => {
  return useQuery({
    ...employeePerformanceReviewKey.searchReviewParticipantViews(employeeId, filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiEmployeePerformanceReviews.searchReviewParticipantViews(
        employeeId,
        filter,
        signal
      );
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });
};
